/*
 * StackSchematic Guide, docked into the Backstage viewer.
 *
 * Guide is loaded in a frame from its own origin rather than bundled into this app. That is an
 * architectural choice, not a packaging convenience — see
 * `docs/decisions/0009-guide-embeds-as-a-frame.md`. The short version: CLAUDE.md forbids Guide,
 * CopilotKit, Claude and AG-UI from leaking into the six evidence-bearing products, and the
 * Backstage viewer is the Catalog surface. A frame makes that structural. With Guide switched
 * off, this file renders `null` and the viewer contains no Guide code at all.
 *
 * What crosses the boundary is only what the host knows and Guide cannot: the route the user is
 * on, and who they are. Both go over `postMessage` to Guide's exact origin.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { configApiRef, identityApiRef, useApi } from '@backstage/frontend-plugin-api';
import { contextForLocation, type GuidePageContext } from './pageContext';
import { guideFrameSrc } from './guideFrameSrc';

/**
 * How often the route is re-read.
 *
 * `AppRootElementBlueprint` renders outside the app layout, so this component cannot rely on
 * being inside the router and cannot use `useLocation`. `popstate` does not fire for the
 * `pushState` navigations a single-page app makes, so polling the URL is what actually works
 * regardless of the router's internals. It reads one string.
 */
const ROUTE_POLL_MS = 750;

/**
 * How often the host's identity token is re-read and re-sent.
 *
 * Backstage's identity tokens live an hour and `getCredentials()` refreshes them transparently.
 * Ten minutes keeps the frame holding a valid one without making the refresh a visible event.
 */
const IDENTITY_REFRESH_MS = 10 * 60 * 1000;

function useCurrentPath(): string {
  const [path, setPath] = useState(() => window.location.pathname);
  useEffect(() => {
    const tick = (): void => {
      setPath(current => (current === window.location.pathname ? current : window.location.pathname));
    };
    const timer = window.setInterval(tick, ROUTE_POLL_MS);
    window.addEventListener('popstate', tick);
    return () => {
      window.clearInterval(timer);
      window.removeEventListener('popstate', tick);
    };
  }, []);
  return path;
}

export function GuideWidget(): React.ReactElement | null {
  const config = useApi(configApiRef);
  const identity = useApi(identityApiRef);

  const guideUrl = config.getOptionalString('stackschematic.guide.url');
  const enabled = config.getOptionalBoolean('stackschematic.guide.enabled') ?? Boolean(guideUrl);

  const [open, setOpen] = useState(false);
  const [ready, setReady] = useState(false);
  const [actor, setActor] = useState<string>('unknown');
  const [hostToken, setHostToken] = useState<{
    token: string;
    email?: string;
    displayName?: string;
  } | null>(null);
  const frame = useRef<HTMLIFrameElement>(null);
  const path = useCurrentPath();

  const guideOrigin = useMemo(() => {
    if (!guideUrl) return null;
    try {
      return new URL(guideUrl).origin;
    } catch {
      return null;
    }
  }, [guideUrl]);

  const context: GuidePageContext[] = useMemo(() => contextForLocation(path), [path]);

  // Who to record against an approval. Guide's execute-class tools require an attributable
  // human, so this is not decoration - it is the name that ends up in the audit trail.
  useEffect(() => {
    let cancelled = false;
    identity
      .getBackstageIdentity()
      .then(id => {
        if (!cancelled) setActor(id.userEntityRef);
      })
      .catch(() => {
        // An unidentified user still gets a working chat; they just cannot approve anything
        // under a name we can stand behind, which is the correct outcome.
      });
    return () => {
      cancelled = true;
    };
  }, [identity]);

  /*
   * The host's own identity token, so StackSchematic can establish who this is rather than take
   * the host's word for it. It is a signed assertion; the control plane verifies it. Sending it
   * is what turns the actor recorded against an approval from a claim into something checkable.
   */
  useEffect(() => {
    let cancelled = false;
    const read = async (): Promise<void> => {
      try {
        const [credentials, profile] = await Promise.all([
          identity.getCredentials(),
          identity.getProfileInfo().catch(() => ({}) as { email?: string; displayName?: string }),
        ]);
        // A guest or mocked user has no verified identity and therefore no token. That is a
        // supported state: Guide falls back to the unverified claim below.
        if (!cancelled && credentials.token) {
          setHostToken({
            token: credentials.token,
            ...(profile.email ? { email: profile.email } : {}),
            ...(profile.displayName ? { displayName: profile.displayName } : {}),
          });
        }
      } catch {
        // Leave identity null; the dock still works, unverified.
      }
    };
    void read();
    const timer = window.setInterval(() => void read(), IDENTITY_REFRESH_MS);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [identity]);

  // Guide announces itself when it mounts; only then is there anything to post to.
  useEffect(() => {
    if (!guideOrigin) return undefined;
    const onMessage = (event: MessageEvent): void => {
      if (event.origin !== guideOrigin) return;
      if ((event.data as { type?: string } | null)?.type === 'stackschematic:ready') setReady(true);
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [guideOrigin]);

  const send = useCallback(() => {
    if (!ready || !guideOrigin) return;
    const target = frame.current?.contentWindow;
    if (!target) return;
    // Always to the exact origin, never '*': the second argument is what stops the message
    // being delivered if something else has navigated the frame.
    target.postMessage({ type: 'stackschematic:context', context, actor }, guideOrigin);
    if (hostToken) {
      target.postMessage({ type: 'stackschematic:identity', ...hostToken }, guideOrigin);
    }
  }, [ready, guideOrigin, context, actor, hostToken]);

  useEffect(send, [send]);

  // The optionality switch. No URL configured means no Guide, and nothing is loaded.
  const frameSrc = guideFrameSrc(guideUrl);
  if (!enabled || !guideUrl || !guideOrigin || !frameSrc) return null;

  if (!open) {
    return (
      /*
       * No aria-label. The visible text is already the accessible name, and a label that does
       * not contain the visible text makes the two disagree — the WCAG 2.5.3 failure, where a
       * speech-input user asking for "Ask Guide" cannot reach the button labelled "Ask Guide".
       */
      <button type="button" className="ss-guide-launcher" onClick={() => setOpen(true)}>
        Ask Guide
      </button>
    );
  }

  return (
    <aside className="ss-guide-dock" aria-label="StackSchematic Guide">
      <header className="ss-guide-dock-head">
        <span className="ss-guide-dock-title">Guide</span>
        {/* This one keeps a label: "×" is not a name anything can announce or ask for. */}
        <button
          type="button"
          className="ss-guide-dock-close"
          onClick={() => setOpen(false)}
          aria-label="Close Guide"
        >
          ×
        </button>
      </header>
      <iframe
        ref={frame}
        className="ss-guide-frame"
        title="StackSchematic Guide"
        src={frameSrc}
        // Guide needs scripts and its own origin (for the agent fetch); it needs nothing else.
        // No allow-top-navigation, so a framed page cannot navigate the viewer away.
        sandbox="allow-scripts allow-same-origin allow-forms"
      />
    </aside>
  );
}
